import { CrmIntegrationsResponse } from '@appTypes/CrmIntegrationsResponse';
import { DealerState } from '@appTypes/DealerState';
import AMLogoMobile from '@assets/svgs/fritz-light.svg';
import Logo from '@assets/svgs/logo-marketeyeq-wht.svg';
import { CrmIntegrationNames } from '@enums/CrmIntegrationNamesType';
import { RoutePaths } from '@enums/RoutePaths';
import { useHeader } from '@hooks/header/UseHeader';
import { useCheckDevice } from '@hooks/UseCheckDevice';
import { useConfig } from '@hooks/UseConfig';
import { usePwa } from '@hooks/UsePwa';
import { useTimer } from '@hooks/UseTimer';
import { useUserDealer } from '@hooks/UseUserDealer';
import { logoutRedirect } from '@services/auth/AuthService';
import { DataExportService } from '@services/dataexport/DataExportService';
import { HeaderService } from '@services/shared/HeaderService';
import { AppState } from '@store/AppState';
import {
  SetEEQConquestPrivateIncentiveCount,
  SetPrivateIncentiveCount,
  setIsCrmPageAvailable,
} from '@store/dealer/DealerActions';
import { setLeftMobileMenu } from '@store/portfolio/PortfolioActions';
import { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import NotificationContainer from '../notifications/NotificationContainer';
import FavouriteDeals from './favorite-deals/FavoriteDeals';
import { HeaderContainer, NavbarHeader, NotificationButton } from './HeaderStyles';
import IntercomContainer from './intercom/IntercomContainer';
import RecentDeals from './recent-deals/RecentDeals';
import SearchDeals from './search-deals/SearchDeals';
import UserMenuHeader from './user-menu-header/UserMenuHeader';

const getIpAuthorized = () => {
  return (localStorage.getItem('ipAuthorized') &&
    localStorage.getItem('ipAuthorized') === 'true') as boolean;
};

export const Header: FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const logout = () => logoutRedirect();
  const { featureToggle } = useSelector<AppState, DealerState>((state) => state.dealer);
  const { isMobileDevice } = useCheckDevice();
  const [isIpAuthorized, setIsIpAuthorized] = useState<boolean>(false);
  const [hasPrivateIncentive, setHasPrivateIncentive] = useState<boolean>(false);
  const [hasEEQConquestPrivateIncentive, setHasEEQConquestPrivateIncentive] =
    useState<boolean>(false);
  const currentUrl = window.location.pathname;
  const { currentHomePage, isActive, isReportingRoute, isUpdatingPredictionsRoute } =
    useHeader(currentUrl);
  const { isIncentiveAdvantageType } = useUserDealer();
  const isPerformanceInsightsEnabled = featureToggle.performanceInsights;
  const isServicePortfolioReportEnabled = featureToggle.servicePortfolioReport;
  const showInactiveDeals = featureToggle.showInactiveDeals;
  const isIntercomEnabled =
    location.hostname !== 'localhost' &&
    location.hostname !== '127.0.0.1' &&
    featureToggle.intercom;

  const { isPwa } = usePwa();
  const { pwaMfaTimer } = useConfig();
  const { startTimer, resetTimer, timeout } = useTimer(Number(pwaMfaTimer));

  const handleIconClick = (): void => {
    document.body.style.overflow = 'hidden';
    dispatch(setLeftMobileMenu(true));
  };

  useEffect(() => {
    const handleContextMenu = (event: MouseEvent) => {
      event.preventDefault();
    };
    if (isPwa) {
      document.body.classList.add('disable-long-press');
      document.addEventListener('contextmenu', handleContextMenu);
    }

    DataExportService.getPrivateIncentiveDataCount().then((response) => {
      const privateIncentiveByDealers: any = response;
      //display only those dealers who have private incentives
      if (privateIncentiveByDealers && Object.keys(privateIncentiveByDealers).length > 0) {
        const dealerId: string[] = Object.keys(privateIncentiveByDealers);
        const count: number[] = Object.values(privateIncentiveByDealers);
        dispatch(SetPrivateIncentiveCount({ dealerId: dealerId, count: count }));
        setHasPrivateIncentive(true);
        window.localStorage.setItem('hasPrivateIncentive', JSON.stringify(true));
      } else {
        window.localStorage.setItem('hasPrivateIncentive', JSON.stringify(false));
      }
    });

    DataExportService.getEEQConquestIncentiveDataCount().then((response) => {
      const eeqConquestPrivateIncentiveByDealers: any = response;
      if (
        eeqConquestPrivateIncentiveByDealers &&
        Object.keys(eeqConquestPrivateIncentiveByDealers).length > 0
      ) {
        const dealerId: string[] = Object.keys(eeqConquestPrivateIncentiveByDealers);
        const count: number[] = Object.values(eeqConquestPrivateIncentiveByDealers);
        dispatch(SetEEQConquestPrivateIncentiveCount({ dealerId: dealerId, count: count }));
        setHasEEQConquestPrivateIncentive(true);
        window.localStorage.setItem('hasEEQConquestPrivateIncentive', JSON.stringify(true));
      } else {
        window.localStorage.setItem('hasEEQConquestPrivateIncentive', JSON.stringify(false));
      }
    });

    HeaderService.getCrmIntegrations().then((res: CrmIntegrationsResponse) => {
      const anyDealerIntegratedWith = new Map<string, boolean>();
      const keys = [
        CrmIntegrationNames.ELEAD,
        CrmIntegrationNames.VIN_SOLUTION,
        CrmIntegrationNames.DEALERSOCKET,
      ];
      keys.forEach((key) => {
        const isCrmAvailable = res[key] && !!(res[key] as string[]).length;
        anyDealerIntegratedWith.set(key, isCrmAvailable);
      });

      const IsCrmPageAvailable =
        anyDealerIntegratedWith.get(CrmIntegrationNames.DEALERSOCKET) ||
        (anyDealerIntegratedWith.get(CrmIntegrationNames.ELEAD) && featureToggle.crmMapper);
      dispatch(setIsCrmPageAvailable(IsCrmPageAvailable));
    });

    const handleChangeStorage = () => {
      setIsIpAuthorized(getIpAuthorized());
    };

    window.addEventListener('storage', handleChangeStorage);

    return () => {
      window.removeEventListener('storage', handleChangeStorage);
      document.removeEventListener('contextmenu', handleContextMenu);
    };
  }, []);

  useEffect(() => {
    if (isPwa && isIpAuthorized) {
      const events = [
        'mousedown',
        'mousemove',
        'keypress',
        'keydown',
        'click',
        'scroll',
        'touchstart',
      ];
      events.forEach((event) => window.addEventListener(event, startTimer));
      startTimer(); // Start the timer initially

      return () => {
        events.forEach((event) => window.removeEventListener(event, startTimer));
      };
    } else {
      resetTimer();
      return () => {
        const events = [
          'mousedown',
          'mousemove',
          'keypress',
          'keydown',
          'click',
          'scroll',
          'touchstart',
        ];
        events.forEach((event) => window.removeEventListener(event, startTimer));
      };
    }
  }, [isIpAuthorized]);

  useEffect(() => {
    if (isPwa && timeout && isIpAuthorized) {
      logout();
    }
  }, [timeout]);

  if (isMobileDevice) {
    document.body.classList.add('shell-with-nav-mobile');
  } else {
    document.body.classList.remove('shell-with-nav-mobile');
  }

  return (
    <HeaderContainer data-testid='header-container'>
      <header className='navbar navbar-expand'>
        <div className={`collapse navbar-collapse`}>
          <NavbarHeader isMobileDevice={isMobileDevice} data-testid='am-logo'>
            {!isMobileDevice ? (
              <a onClick={() => navigate(currentHomePage())}>
                <img src={Logo} />
              </a>
            ) : (
              <a onClick={handleIconClick}>
                <img src={AMLogoMobile} />
              </a>
            )}
          </NavbarHeader>
          {!isMobileDevice && !isUpdatingPredictionsRoute() && !isReportingRoute() && (
            <>
              <ul className='nav navbar-nav deal-icon-menu'>
                <RecentDeals isIncentiveAdvantageType={isIncentiveAdvantageType} />
                {!isIncentiveAdvantageType && <FavouriteDeals />}
              </ul>
              <div className='nav navbar-nav top-search'>
                <SearchDeals
                  isIncentiveAdvantageType={isIncentiveAdvantageType}
                  showInactiveDeals={showInactiveDeals ?? false}
                />
              </div>
            </>
          )}
          {!isMobileDevice && isReportingRoute() && (
            <ul className='nav navbar-nav filter_menu reports-nav'>
              {isPerformanceInsightsEnabled && !isIncentiveAdvantageType && (
                <li className={isActive('performanceInsights') ? 'active active-background' : ''}>
                  <a
                    className={
                      isActive('performanceInsights')
                        ? 'reports-nav-button reports-nav-button-active'
                        : 'reports-nav-button'
                    }
                    onClick={() => navigate(RoutePaths.PerformanceInsights)}
                  >
                    Performance Insights
                  </a>
                </li>
              )}
              {isPerformanceInsightsEnabled &&
                (isIncentiveAdvantageType || hasPrivateIncentive) && (
                  <li
                    className={
                      isActive('privateIncentiveInsights') ? 'active active-background' : ''
                    }
                  >
                    <a
                      className={
                        isActive('privateIncentiveInsights')
                          ? 'reports-nav-button reports-nav-button-active'
                          : 'reports-nav-button'
                      }
                      onClick={() => navigate(RoutePaths.PrivateIncentiveInSights)}
                    >
                      Private Incentive Insights
                    </a>
                  </li>
                )}
              {isPerformanceInsightsEnabled &&
                !isIncentiveAdvantageType &&
                isServicePortfolioReportEnabled && (
                  <li className={isActive('service-portfolio') ? 'active active-background' : ''}>
                    <a
                      className={
                        isActive('service-portfolio')
                          ? 'reports-nav-button reports-nav-button-active'
                          : 'reports-nav-button'
                      }
                      onClick={() => navigate(RoutePaths.ServicePortfolio)}
                    >
                      Service Portfolio Insights
                    </a>
                  </li>
                )}
            </ul>
          )}
          <ul className='nav user-menu navbar-nav'>
            {!isMobileDevice && (
              <li className='user-menu-button'>
                <UserMenuHeader />
              </li>
            )}
            {!isMobileDevice &&
              isIntercomEnabled &&
              !isUpdatingPredictionsRoute() &&
              !isReportingRoute() && (
                <li className='navbar-button navbar-intercom'>
                  <IntercomContainer isIntercomEnabled={isIntercomEnabled ?? false} />
                </li>
              )}
            <NotificationButton>
              <NotificationContainer />
            </NotificationButton>
            {isMobileDevice && (
              <li className='mobile-search'>
                <SearchDeals
                  isIncentiveAdvantageType={isIncentiveAdvantageType}
                  showInactiveDeals={showInactiveDeals ?? false}
                />
              </li>
            )}
          </ul>
        </div>
      </header>
    </HeaderContainer>
  );
};
